/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2 } from '@coursera/cds-core';
import { StarFilledIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/reviews';

const styles = {
  card: css`
    width: 100%;
    min-width: 0;
    flex: 1;
    min-height: 0;
    padding: var(--cds-spacing-300);
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-grey-300);
    border-radius: var(--cds-border-radius-100);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    box-sizing: border-box;
  `,
  authorName: css`
    margin: 0 0 var(--cds-spacing-100);
    font-weight: 700;
    color: var(--cds-color-neutral-primary);
  `,
  ratingLine: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
    margin: 0 0 var(--cds-spacing-200);
    color: var(--cds-color-neutral-primary);
  `,
  ratingWrapper: css`
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  ratingDate: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  dotSeparator: css`
    color: var(--cds-color-neutral-primary-weak);
    margin: 0 var(--cds-spacing-50);
  `,
  reviewText: css`
    margin: 0 0 var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary);
  `,
};

export type TopReviewCardReview = {
  authorName: string;
  comment: string;
  rating: number;
  timestamp: string;
};

type Props = {
  review: TopReviewCardReview;
};

const TopReviewCarouselCard = ({ review }: Props) => {
  const { authorName, comment, rating, timestamp } = review;

  const formattedDate = formatDateTimeDisplay(timestamp, MED_DATE_ONLY_DISPLAY);

  return (
    <article css={styles.card} data-testid="top-review-carousel-card">
      <Typography2 component="p" variant="bodyPrimary" css={styles.authorName}>
        {authorName}
      </Typography2>
      <div css={styles.ratingLine}>
        <span
          role="img"
          aria-roledescription="rating"
          aria-label={_t('#{rating} stars', { rating })}
          css={styles.ratingWrapper}
        >
          <StarFilledIcon color="default" size="medium" aria-hidden="true" />
          <Typography2 component="span" variant="subtitleSmall" aria-hidden="true">
            {rating.toFixed(1)}
          </Typography2>
        </span>
        <span css={styles.dotSeparator} aria-hidden="true">
          ·
        </span>
        <Typography2 component="span" variant="bodySecondary" css={styles.ratingDate}>
          <FormattedMessage message={_t('Reviewed {date}')} date={formattedDate} />
        </Typography2>
      </div>
      <Typography2 component="p" variant="bodySecondary" css={styles.reviewText}>
        {comment}
      </Typography2>
    </article>
  );
};

export default TopReviewCarouselCard;
